import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Tooltip from '@mui/material/Tooltip';

import { useState } from 'react';

import fetchRunDetails from 'store/fetchRunDetails';
import fetchWorkflowProgress from 'store/fetchWorkflowProgress';
import pauseRun from 'store/pauseRun';
import resumeRun from 'store/resumeRun';

import RunState from 'types/RunState';

const OPEN_STATES: RunState[] = ['RUNNING', 'READY', 'WAITING', 'RESERVED', 'PAUSING'];

export default function PauseResumeButtons({
  runId,
  paused,
  taskStates,
  onMessage,
}: {
  runId: string;
  paused: boolean;
  taskStates: RunState[];
  onMessage: (message: string) => void;
}) {
  const [busy, setBusy] = useState<null | 'pause' | 'resume'>(null);

  const anyOpen = taskStates.some((state) => OPEN_STATES.includes(state));
  const anyPausing = taskStates.includes('PAUSING');

  const refresh = () => {
    fetchRunDetails(runId);
    fetchWorkflowProgress(runId);
  };

  return (
    <>
      {!paused && (
        <Tooltip
          title={
            anyOpen
              ? 'Pause the workflow: nothing new starts, and running tasks that support pause checkpoint and free their worker slot; other running tasks finish first'
              : 'Nothing left to pause'
          }
          placement="bottom-start"
        >
          <span>
            <Button
              variant="outlined"
              disabled={!anyOpen || busy !== null}
              onClick={() => {
                setBusy('pause');
                pauseRun(runId)
                  .then((summary) => {
                    const pausing = summary.task_state_count?.PAUSING ?? 0;
                    onMessage(
                      pausing > 0
                        ? `Workflow paused — ${pausing} task(s) checkpointing, nothing new will start`
                        : 'Workflow paused — nothing new will start',
                    );
                    refresh();
                  })
                  .catch(() => onMessage('Pause failed'))
                  .finally(() => setBusy(null));
              }}
            >
              {busy === 'pause' && <CircularProgress size={16} sx={{ mr: 1 }} />}
              {anyPausing ? 'Pausing…' : 'Pause'}
            </Button>
          </span>
        </Tooltip>
      )}
      {paused && (
        <>
          <Chip size="small" color="warning" label={anyPausing ? 'Pausing…' : 'Paused'} sx={{ alignSelf: 'center' }} />
          <Tooltip
            title="Resume the workflow: queued tasks dispatch again and paused tasks re-queue, each pinned to the node holding its checkpoint"
            placement="bottom-start"
          >
            <span>
              <Button
                variant="contained"
                color="warning"
                disabled={busy !== null}
                onClick={() => {
                  setBusy('resume');
                  resumeRun(runId)
                    .then(() => {
                      onMessage('Workflow resumed');
                      refresh();
                    })
                    .catch(() => onMessage('Resume failed'))
                    .finally(() => setBusy(null));
                }}
              >
                {busy === 'resume' && <CircularProgress size={16} sx={{ mr: 1 }} />}
                Resume
              </Button>
            </span>
          </Tooltip>
        </>
      )}
    </>
  );
}
